import React, { useState, useEffect, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import type { StylesConfig, SingleValue } from 'react-select';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { employeeApi, locationApi, positionApi, scheduleApi, payrollDepartmentApi, payrollEmploymentTypesApi, roleApi, employeeLicenseApi, payRecordApi, feedbackRecordApi, trainingRecordApi, trainingRecordCycleApi, trainingTypeApi, paymentTypeApi, paymentFrequencyApi, assignLaborCostByOptionApi, workingDayApi, employeeNoteApi, leavePoliciesApi, employeeLeaveBalanceApi, faceApi, getProfileImageUrl, authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Employee, Location, Position, Schedule, PayrollDepartment, PayrollEmploymentType, Role, UpdateEmployeeRequest, EmployeeLicense, PayRecord, FeedbackRecord, TrainingRecord, TrainingRecordCycle, TrainingType, PaymentType, PaymentFrequency, AssignLaborCostByOption, WorkingDay, CreatePayRecordRequest, CreateFeedbackRecordRequest, CreateTrainingRecordRequest, CreateTrainingRecordCycleRequest, UpdateTrainingRecordCycleRequest, EmployeeNote, CreateEmployeeNoteRequest, UpdateEmployeeNoteRequest, LeavePolicy, EmployeeLeaveBalance, EmployeeMfaStatus } from '../types';
import { ArrowLeft, Save, User, Lock, Briefcase, FileText, Award, Currency, Receipt, MessageSquare, GraduationCap, Plus, Edit, Trash2, X, Paperclip, Loader2, Calendar, Scan, Network, Building2, Eye, Download } from 'lucide-react';
import BasketSelect from '../components/BasketSelect';
import EmployeeHierarchyModal from '../components/EmployeeHierarchyModal';
import EmployeeLicensesTab from '../components/EmployeeLicensesTab';
import { useToast, ToastContainer } from '../components/Toast';
import PayrollAndPayRecords from '../components/PayrollAndPayRecords';
import { appConfirm } from '../context/SystemDialogContext';
import {
  formatLeaveBalanceAmount,
  getDisplayAccrued,
  getDisplayCarriedForward,
  getDisplayEntitled,
  getDisplayPending,
  getDisplayRemaining,
  getDisplayTaken,
  getRemainingLabel,
  leaveBalanceUnit,
  shouldShowEntitlement,
} from '../utils/leaveBalanceDisplay';
import { formatDateOnly } from '../utils/datetime';
import { formatCompanyDate, formatCompanyDateLocale, formatCompanyDateTime } from '../utils/companyDateFormat';

type LineManagerOption = { value: number; label: string };

const lineManagerAsyncStyles: StylesConfig<LineManagerOption, false> = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#9ca3af' },
    minHeight: '38px',
    fontSize: '14px',
  }),
  placeholder: (provided) => ({ ...provided, color: '#9ca3af' }),
  input: (provided) => ({ ...provided, color: '#374151' }),
};

const EmployeeProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { employee: currentUser, updateEmployee } = useAuth();
  const [searchParams] = useSearchParams();
  const activeTabParam = searchParams.get('tab') || 'login';

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [payrollDepartments, setPayrollDepartments] = useState<PayrollDepartment[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [payrollEmploymentTypes, setPayrollEmploymentTypes] = useState<PayrollEmploymentType[]>([]);
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [licenses, setLicenses] = useState<EmployeeLicense[]>([]);
  const [payRecords, setPayRecords] = useState<PayRecord[]>([]);
  const [feedbackRecords, setFeedbackRecords] = useState<FeedbackRecord[]>([]);
  const [trainingRecords, setTrainingRecords] = useState<TrainingRecord[]>([]);
  const [employeeNotes, setEmployeeNotes] = useState<EmployeeNote[]>([]);
  const [leavePolicies, setLeavePolicies] = useState<LeavePolicy[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<EmployeeLeaveBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState<Record<string, boolean>>({});
  const [tabsLoaded, setTabsLoaded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(activeTabParam);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const [formData, setFormData] = useState<UpdateEmployeeRequest>({
    email: '',
    firstName: '',
    lastName: '',
    pin: '',
    isActive: true,
    subLocationIds: [],
    subPositionIds: [],
    scheduleIds: [],
    appRoleIds: [],
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletingFaceId, setDeletingFaceId] = useState(false);
  const [showProfileImageModal, setShowProfileImageModal] = useState(false);
  const [lineManagerSelectValue, setLineManagerSelectValue] = useState<LineManagerOption | null>(null);
  const [hierarchyModalOpen, setHierarchyModalOpen] = useState(false);
  const [mfaStatus, setMfaStatus] = useState<EmployeeMfaStatus | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaExemptHours, setMfaExemptHours] = useState(24);
  const [mfaExemptNote, setMfaExemptNote] = useState('');
  const [mfaActionLoading, setMfaActionLoading] = useState(false);

  const loadLineManagerOptions = useCallback(
    async (inputValue: string): Promise<LineManagerOption[]> => {
      if (!id) return [];
      const rows = await employeeApi.getLineManagerOptions(parseInt(id, 10), inputValue || undefined);
      return rows.map((r) => ({ value: r.id, label: r.label }));
    },
    [id]
  );

  useEffect(() => {
    if (id) {
      fetchCoreData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setActiveTab(activeTabParam);
  }, [activeTabParam]);

  useEffect(() => {
    if (!id || activeTab !== 'login') return;
    let cancelled = false;
    const load = async () => {
      setMfaLoading(true);
      setMfaError(null);
      try {
        const status = await authApi.mfa.getEmployee(parseInt(id, 10));
        if (!cancelled) setMfaStatus(status);
      } catch (err: any) {
        if (!cancelled) {
          setMfaStatus(null);
          setMfaError(err?.response?.data?.message || err?.message || 'Failed to load MFA status.');
        }
      } finally {
        if (!cancelled) setMfaLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, activeTab]);

  useEffect(() => {
    if (!showProfileImageModal) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowProfileImageModal(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showProfileImageModal]);

  // Lazy-load tab data when user switches tabs
  useEffect(() => {
    if (!id || !employee || tabsLoaded.has(activeTab)) return;
    if (activeTab === 'login' || activeTab === 'personal') return;
    fetchTabData(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, employee, id]);

  // Scroll to visa details when tab is personal and hash/param is present
  useEffect(() => {
    if (loading) return; // Don't scroll while loading
    
    const shouldScroll = activeTab === 'personal' && 
      (window.location.hash === '#visa-details' || searchParams.get('scrollToVisa') === 'true');
    
    if (!shouldScroll) return;
    
    // Use a more robust scrolling approach with multiple retries
    let retryCount = 0;
    const maxRetries = 30; // Try for up to 3 seconds (30 * 100ms)
    
    const attemptScroll = () => {
      // Check if the form with personal tab is actually rendered
      const formElement = document.querySelector('form');
      const visaSection = document.getElementById('visa-details');
      
      if (visaSection && formElement) {
        // Element found - scroll to it using scrollIntoView (more reliable)
        try {
          visaSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Also adjust position slightly to account for header
          setTimeout(() => {
            const currentPosition = window.pageYOffset;
            const targetPosition = Math.max(0, currentPosition - 100);
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }, 300);
          
          console.log('[EmployeeProfile] Successfully scrolled to visa-details section');
          return; // Success - stop retrying
        } catch (error) {
          console.warn('[EmployeeProfile] Error scrolling:', error);
          // Fallback: try simple scroll
          const elementPosition = visaSection.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo(0, Math.max(0, elementPosition - 100));
          return;
        }
      } else {
        // Element not found yet - retry
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(attemptScroll, 100);
        } else {
          console.warn('[EmployeeProfile] Could not find visa-details element after', maxRetries, 'retries. Form exists:', !!formElement, 'Visa section exists:', !!visaSection);
        }
      }
    };
    
    // Start attempting to scroll after a longer delay to ensure form is rendered
    setTimeout(() => {
      attemptScroll();
    }, 500);
  }, [activeTab, searchParams, loading]); // Include loading in dependencies

  const fetchCoreData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const employeeId = parseInt(id);
      const [employeeData, employmentTypesData] = await Promise.all([
        employeeApi.getById(employeeId),
        payrollEmploymentTypesApi.list(),
      ]);
      setEmployee(employeeData);
      setPayrollEmploymentTypes(employmentTypesData);
      initFormData(employeeData);
      setTabsLoaded(new Set(['login', 'personal']));
    } catch (error) {
      console.error('Error fetching employee:', error);
      setError('Failed to load employee data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const initFormData = (employeeData: Employee) => {
    setFormData({
        email: employeeData.email || '',
        title: employeeData.title || '',
        firstName: employeeData.firstName || '',
        lastName: employeeData.lastName || '',
        pin: employeeData.pin || '',
        isActive: employeeData.isActive ?? true,
        password: '',
        passwordQuestion: employeeData.passwordQuestion || '',
        passwordAnswer: employeeData.passwordAnswer || '',
        gender: employeeData.gender || '',
        genderOther: employeeData.genderOther || '',
        birthday: employeeData.birthday ? employeeData.birthday.split('T')[0] : '',
        phone: employeeData.phone || '',
        address1: employeeData.address1 || '',
        address2: employeeData.address2 || '',
        address3: employeeData.address3 || '',
        county: employeeData.county || '',
        zipCode: employeeData.zipCode || '',
        psaLicense: employeeData.psaLicense || '',
        startDate: employeeData.startDate ? employeeData.startDate.split('T')[0] : '',
        employmentEndDate: employeeData.employmentEndDate
          ? employeeData.employmentEndDate.split('T')[0]
          : '',
        probationEndDate: employeeData.probationEndDate
          ? employeeData.probationEndDate.split('T')[0]
          : '',
        comment: employeeData.comment || '',
        mustChangePassword: employeeData.mustChangePassword ?? false,
        visaNumber: employeeData.visaNumber || '',
        visaExpiryDate: employeeData.visaExpiryDate ? employeeData.visaExpiryDate.split('T')[0] : '',
        mainLocationId: employeeData.profile?.mainLocationId || undefined,
        mainPositionId: employeeData.profile?.mainPositionId || undefined,
        payrollDepartmentId: employeeData.profile?.payrollDepartmentId || undefined,
        subLocationIds: (employeeData.profile?.subLocationIds && Array.isArray(employeeData.profile.subLocationIds))
          ? employeeData.profile.subLocationIds.map(id => Number(id)).filter(id => !isNaN(id))
          : [],
        subPositionIds: (employeeData.profile?.subPositionIds && Array.isArray(employeeData.profile.subPositionIds))
          ? employeeData.profile.subPositionIds.map(id => Number(id)).filter(id => !isNaN(id))
          : [],
        scheduleIds: (employeeData.profile?.scheduleIds && Array.isArray(employeeData.profile.scheduleIds))
          ? employeeData.profile.scheduleIds.map(id => Number(id)).filter(id => !isNaN(id))
          : [],
        appRoleIds: (employeeData.profile?.appRoleIds && Array.isArray(employeeData.profile.appRoleIds))
          ? employeeData.profile.appRoleIds.map(id => Number(id)).filter(id => !isNaN(id))
          : [],
        allowMobileClockIn: employeeData.profile?.allowMobileClockIn ?? true,
        isManager: employeeData.profile?.isManager ?? false,
        employmentType: employeeData.profile?.employmentType || '',
        payrollEmploymentTypeId: employeeData.profile?.payrollEmploymentTypeId ?? undefined,
        // Leave policy
        leavePolicyId: employeeData.leavePolicyId || undefined,
        // Payroll fields
        worksNumber: employeeData.payroll?.worksNumber || '',
        ppsNumber: employeeData.payroll?.ppsNumber || '',
        ban: employeeData.payroll?.ban || '',
        bic: employeeData.payroll?.bic || '',
        accountNumber: employeeData.payroll?.accountNumber || '',
        sortCode: employeeData.payroll?.sortCode || '',
        accountName: employeeData.payroll?.accountName || '',
        bankName: employeeData.payroll?.bankName || '',
        paymentType: employeeData.payroll?.paymentType || '',
        hourlyRate: employeeData.payroll?.hourlyRate || undefined,
        sundayRate: employeeData.payroll?.sundayRate || undefined,
        bankHolidayRate: employeeData.payroll?.bankHolidayRate || undefined,
        isPremiumPayEligible: employeeData.payroll?.isPremiumPayEligible ?? true,
        allowAdditiveHourlyPay: employeeData.payroll?.allowAdditiveHourlyPay ?? false,
        lineManagerEmployeeId: employeeData.profile?.lineManagerEmployeeId ?? undefined,
      });
    const lmId = employeeData.profile?.lineManagerEmployeeId;
    const lm = employeeData.profile?.lineManager;
    setLineManagerSelectValue(
      lmId != null && lm
        ? { value: lmId, label: `${lm.firstName} ${lm.lastName}`.trim() }
        : lmId != null
          ? { value: lmId, label: `Employee #${lmId}` }
          : null
    );
  };

  const fetchTabData = async (tab: string) => {
    if (!id) return;
    const employeeId = parseInt(id);
    setTabLoading(prev => ({ ...prev, [tab]: true }));
    try {
      const currentYear = new Date().getFullYear();
      switch (tab) {
        case 'work': {
          const [locationsData, positionsData, schedulesData, payrollDepartmentsData, rolesData] = await Promise.all([
            locationApi.getAll(),
            positionApi.getAll(),
            scheduleApi.getAll(),
            payrollDepartmentApi.getAll(),
            roleApi.getAll(),
          ]);
          setLocations(locationsData);
          setPositions(positionsData);
          setSchedules(schedulesData);
          setPayrollDepartments(payrollDepartmentsData);
          setRoles(rolesData);
          break;
        }
        case 'payroll': {
          const [payRecordsData, locationsData, positionsData, schedulesData, payrollDepartmentsData, rolesData] = await Promise.all([
            payRecordApi.getByEmployeeId(employeeId),
            locationApi.getAll(),
            positionApi.getAll(),
            scheduleApi.getAll(),
            payrollDepartmentApi.getAll(),
            roleApi.getAll(),
          ]);
          setPayRecords(payRecordsData);
          setLocations(locationsData);
          setPositions(positionsData);
          setSchedules(schedulesData);
          setPayrollDepartments(payrollDepartmentsData);
          setRoles(rolesData);
          break;
        }
        case 'leave': {
          const [leavePoliciesData, leaveBalancesData] = await Promise.all([
            leavePoliciesApi.getAll(),
            employeeLeaveBalanceApi.getAll(employeeId, currentYear).catch(() => []),
          ]);
          setLeavePolicies(leavePoliciesData);
          setLeaveBalances(leaveBalancesData);
          break;
        }
        case 'licenses': {
          const [licensesData, trainingTypesData] = await Promise.all([
            employeeLicenseApi.getByEmployeeId(employeeId),
            trainingTypeApi.getAll(),
          ]);
          setLicenses(licensesData);
          setTrainingTypes(trainingTypesData);
          break;
        }
        case 'feedback': {
          const feedbackRecordsData = await feedbackRecordApi.getByEmployeeId(employeeId);
          setFeedbackRecords(feedbackRecordsData);
          break;
        }
        case 'training': {
          const [trainingRecordsData, trainingTypesData] = await Promise.all([
            trainingRecordApi.getByEmployeeId(employeeId),
            trainingTypeApi.getAll(),
          ]);
          setTrainingRecords(trainingRecordsData);
          setTrainingTypes(trainingTypesData);
          break;
        }
        case 'notes': {
          const employeeNotesData = await employeeNoteApi.getByEmployeeId(employeeId);
          setEmployeeNotes(employeeNotesData);
          break;
        }
        default:
          break;
      }
      setTabsLoaded(prev => new Set(Array.from(prev).concat(tab)));
    } catch (error) {
      console.error(`Error fetching ${tab} tab data:`, error);
      toast.error(`Failed to load ${tab} data.`);
    } finally {
      setTabLoading(prev => ({ ...prev, [tab]: false }));
    }
  };

  const fetchData = async (silent = false) => {
    if (!id) return;
    try {
      if (!silent) setLoading(true);
      const employeeId = parseInt(id);
      const currentYear = new Date().getFullYear();
      const [employeeData, locationsData, positionsData, schedulesData, payrollDepartmentsData, rolesData, trainingTypesData, licensesData, payRecordsData, feedbackRecordsData, trainingRecordsData, employeeNotesData, leavePoliciesData] = await Promise.all([
        employeeApi.getById(employeeId),
        locationApi.getAll(),
        positionApi.getAll(),
        scheduleApi.getAll(),
        payrollDepartmentApi.getAll(),
        roleApi.getAll(),
        trainingTypeApi.getAll(),
        employeeLicenseApi.getByEmployeeId(employeeId),
        payRecordApi.getByEmployeeId(employeeId),
        feedbackRecordApi.getByEmployeeId(employeeId),
        trainingRecordApi.getByEmployeeId(employeeId),
        employeeNoteApi.getByEmployeeId(employeeId),
        leavePoliciesApi.getAll()
      ]);
      let leaveBalancesData: EmployeeLeaveBalance[] = [];
      try {
        leaveBalancesData = await employeeLeaveBalanceApi.getAll(employeeId, currentYear);
      } catch {
        // ignore
      }
      setEmployee(employeeData);
      if (currentUser?.id === employeeId) {
        updateEmployee(employeeData);
      }
      setLocations(locationsData);
      setPositions(positionsData);
      setSchedules(schedulesData);
      setPayrollDepartments(payrollDepartmentsData);
      setRoles(rolesData);
      setTrainingTypes(trainingTypesData);
      setLicenses(licensesData);
      setPayRecords(payRecordsData);
      setFeedbackRecords(feedbackRecordsData);
      setTrainingRecords(trainingRecordsData);
      setEmployeeNotes(employeeNotesData);
      setLeavePolicies(leavePoliciesData);
      setLeaveBalances(leaveBalancesData);
      initFormData(employeeData);
      setTabsLoaded(new Set(['login', 'personal', 'work', 'leave', 'licenses', 'payroll', 'feedback', 'training', 'notes']));
    } catch (error) {
      console.error('Error fetching employee data:', error);
      setError('Failed to load employee data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshPayRecords = async () => {
    if (!id) return;
    const payRecordsData = await payRecordApi.getByEmployeeId(parseInt(id, 10));
    setPayRecords(payRecordsData);
  };

  const parseJsonArray = (value: string | string[] | undefined): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(v => v.toString());
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(v => v.toString()) : [];
    } catch {
      return [];
    }
  };

  const sameIdList = (left?: number[], right?: number[]) => {
    const a = [...(left ?? [])].sort((x, y) => x - y);
    const b = [...(right ?? [])].sort((x, y) => x - y);
    return a.length === b.length && a.every((value, index) => value === b[index]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setError(null);
    
    // Validate required fields
    if (!formData.mainLocationId) {
      toast.error('Main Location is required.');
      return;
    }
    if (!formData.mainPositionId) {
      toast.error('Main Position is required.');
      return;
    }
    if (!formData.payrollDepartmentId) {
      toast.error('Payroll Department is required.');
      return;
    }
    const hasCatalogEt =
      formData.payrollEmploymentTypeId != null && formData.payrollEmploymentTypeId > 0;
    const hasCustomEt = (formData.employmentType || '').trim().length > 0;
    if (!hasCatalogEt && !hasCustomEt) {
      toast.error('Employment classification is required (choose a payroll type or Other / custom).');
      return;
    }

    const initialAppRoleIds = (employee?.profile?.appRoleIds ?? [])
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));
    const appRolesChanged = !sameIdList(formData.appRoleIds, initialAppRoleIds);
    if (appRolesChanged && (!formData.appRoleIds || formData.appRoleIds.length === 0)) {
      toast.error('At least one App Role is required.');
      return;
    }

    setSaving(true);

    try {
      // Only include password fields if password is provided and not empty
      const updateData: UpdateEmployeeRequest = { ...formData };
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password;
      }
      if (hasCatalogEt) {
        delete updateData.employmentType;
      } else {
        delete updateData.payrollEmploymentTypeId;
      }
      
      const initialSubLocationIds = (employee?.profile?.subLocationIds ?? [])
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value));
      const initialSubPositionIds = (employee?.profile?.subPositionIds ?? [])
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value));
      const initialScheduleIds = (employee?.profile?.scheduleIds ?? [])
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value));
      const initialLineManagerId = employee?.profile?.lineManagerEmployeeId ?? null;
      const lineManagerChanged = (formData.lineManagerEmployeeId ?? null) !== initialLineManagerId;

      if (sameIdList(formData.subLocationIds, initialSubLocationIds)) {
        delete updateData.subLocationIds;
      } else {
        updateData.updateSubLocations = true;
      }
      if (sameIdList(formData.subPositionIds, initialSubPositionIds)) {
        delete updateData.subPositionIds;
      } else {
        updateData.updateSubPositions = true;
      }
      if (sameIdList(formData.scheduleIds, initialScheduleIds)) {
        delete updateData.scheduleIds;
      } else {
        updateData.updateSchedules = true;
      }
      if (appRolesChanged) {
        updateData.updateAppRoles = true;
      } else {
        delete updateData.appRoleIds;
      }
      if (lineManagerChanged) {
        updateData.updateLineManagerEmployee = true;
        updateData.lineManagerEmployeeId = formData.lineManagerEmployeeId ?? null;
      } else {
        updateData.updateLineManagerEmployee = false;
        delete updateData.lineManagerEmployeeId;
      }

      // Do not PATCH legacy address; keep server value until columns are retired.
      delete (updateData as Record<string, unknown>).address;

      await employeeApi.update(parseInt(id), updateData);
      // Reload employee data without full-page loading spinner (keeps pay record modal open)
      await fetchData(true);
      toast.success('Employee updated successfully!');
      // Clear password fields after successful update
      setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
    } catch (error: any) {
      console.error('Error updating employee:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          'Failed to update employee. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: keyof UpdateEmployeeRequest, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // If main location is changed, remove it from sub-locations if it was selected
      if (field === 'mainLocationId' && value) {
        updated.subLocationIds = (updated.subLocationIds || []).filter(id => id !== value);
      }
      
      // If main position is changed, remove it from sub-positions if it was selected
      if (field === 'mainPositionId' && value) {
        updated.subPositionIds = (updated.subPositionIds || []).filter(id => id !== value);
      }
      
      return updated;
    });
  };

  const handleArrayFieldChange = (field: 'subLocationIds' | 'subPositionIds' | 'scheduleIds' | 'appRoleIds', value: string[]) => {
    const numValues = value.map(v => parseInt(v)).filter(v => !isNaN(v));
    setFormData(prev => {
      const updated = { ...prev, [field]: numValues };
      
      // Prevent main location from being in sub-locations
      if (field === 'subLocationIds' && prev.mainLocationId) {
        updated.subLocationIds = numValues.filter(id => id !== prev.mainLocationId);
      }
      
      // Prevent main position from being in sub-positions
      if (field === 'subPositionIds' && prev.mainPositionId) {
        updated.subPositionIds = numValues.filter(id => id !== prev.mainPositionId);
      }
      
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="w-full max-w-[82%] mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div>Loading employee data...</div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="w-full max-w-[82%] mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Employee not found.</p>
          <button
            onClick={() => navigate('/employees')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Employees
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'login', label: 'Login', icon: Lock },
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'work', label: 'Work', icon: Briefcase },
    { id: 'leave', label: 'Leave', icon: Calendar },
    { id: 'licenses', label: 'Licenses', icon: Award },
    { id: 'payroll', label: 'Payroll & Pay Records', icon: Currency },
    { id: 'feedback', label: 'Feedback', icon: MessageSquare },
    { id: 'training', label: 'Training', icon: GraduationCap },
    { id: 'notes', label: 'Notes', icon: FileText },
  ];

  return (
    <div className="w-full max-w-[82%] mx-auto p-6">
      <ToastContainer toasts={toast.toasts} onClose={toast.removeToast} />
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <button
            onClick={() => navigate('/employees')}
            className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Employees
          </button>
          <Link
            to="/employees/organization-chart"
            className="inline-flex items-center gap-2 text-sm font-medium text-violet-700 hover:text-violet-900 shrink-0"
          >
            <Building2 className="h-4 w-4 shrink-0" />
            Organization chart
          </Link>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between w-full">
          <div className="flex items-center gap-4 min-w-0">
            {employee.profileImageUrl ? (
              <button
                type="button"
                onClick={() => setShowProfileImageModal(true)}
                className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full flex-shrink-0"
              >
                <img
                  src={getProfileImageUrl(employee.profileImageUrl, employee.updatedAt) ?? ''}
                  alt={`${employee.firstName} ${employee.lastName}`}
                  className="w-16 h-16 rounded-full object-cover border-2 border-neutral-200 cursor-pointer hover:opacity-90 transition-opacity"
                />
              </button>
            ) : (
              <div className="w-16 h-16 rounded-full bg-neutral-200 flex items-center justify-center flex-shrink-0">
                <User className="h-8 w-8 text-neutral-500" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-neutral-800">
                {employee.firstName} {employee.lastName}
              </h1>
              <p className="text-neutral-600 mt-1">{employee.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setHierarchyModalOpen(true)}
            className="inline-flex items-center gap-2 self-start sm:self-center text-sm font-medium text-violet-700 hover:text-violet-900 px-3 py-2 rounded-lg border border-violet-200 bg-violet-50/80 hover:bg-violet-100 transition-colors shrink-0"
          >
            <Network className="h-4 w-4 shrink-0" />
            Reporting hierarchy
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  navigate(`/employees/${id}?tab=${tab.id}`, { replace: true });
                }}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'licenses' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          {tabLoading.licenses ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
          <EmployeeLicensesTab
            licenses={licenses}
            employeeId={parseInt(id || '0')}
            onRefresh={fetchData}
          />
          )}
        </div>
      )}

      {activeTab === 'feedback' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          {tabLoading.feedback ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
          <FeedbackTab
            feedbackRecords={feedbackRecords}
            employeeId={parseInt(id || '0')}
            onRefresh={fetchData}
          />
          )}
        </div>
      )}

      {activeTab === 'training' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          {tabLoading.training ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
          <TrainingTab
            trainingRecords={trainingRecords}
            employeeId={parseInt(id || '0')}
            trainingTypes={trainingTypes}
            onRefresh={() => fetchData(true)}
          />
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          {tabLoading.notes ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
          <NotesTab
            employeeNotes={employeeNotes}
            employeeId={parseInt(id || '0')}
            onRefresh={fetchData}
          />
          )}
        </div>
      )}

      {activeTab === 'leave' && (
        <div className="space-y-6">
          {tabLoading.leave ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
        <>
          {/* Leave Policy Assignment */}
          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">Leave Policy Assignment</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Assigned Leave Policy
                </label>
                <select
                  value={formData.leavePolicyId || ''}
                  onChange={(e) => handleFieldChange('leavePolicyId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full md:w-1/2 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- No Policy Assigned --</option>
                  {leavePolicies.map(policy => (
                    <option key={policy.leavePolicyId} value={policy.leavePolicyId}>
                      {policy.policyName} {policy.isDefault && '(Default)'}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-neutral-500">
                  Select the leave policy that applies to this employee
                </p>
              </div>

              {formData.leavePolicyId && leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">
                    {leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.policyName}
                  </h3>
                  <p className="text-sm text-blue-800">
                    {leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.description || 'No description'}
                  </p>
                  <div className="mt-3 text-xs text-blue-700 space-y-1">
                    <div>Leave Year Starts: {formatCompanyDateLocale(new Date(2024, (leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.leaveYearStartMonth || 1) - 1, leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.leaveYearStartDay || 1), { month: 'long', day: 'numeric' })}</div>
                    <div>Carry Forward: {leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.allowCarryForward ? `Yes (up to ${leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.maxCarryForwardDays || 'unlimited'} days)` : 'No'}</div>
                    <div>Minimum Notice: {leavePolicies.find(p => p.leavePolicyId === formData.leavePolicyId)?.minNoticeRequiredDays || 0} days</div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Policy Assignment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Leave Balances */}
          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">
              Leave Balances ({new Date().getFullYear()})
            </h2>
            
            {leaveBalances.length > 0 ? (
              <div className="space-y-4">
                {leaveBalances.map(balance => (
                  <div key={balance.id} className="border border-neutral-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-neutral-900">
                          {balance.leaveType?.name || 'Unknown Leave Type'}
                        </h3>
                        <p className="text-sm text-neutral-600">
                          {balance.leaveType?.description || ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">
                          {getDisplayRemaining(balance)}
                        </div>
                        <div className="text-xs text-neutral-500">{getRemainingLabel(leaveBalanceUnit(balance))}</div>
                      </div>
                    </div>
                    
                    <div className={`grid grid-cols-2 gap-3 text-sm ${shouldShowEntitlement(balance) ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
                      {shouldShowEntitlement(balance) && (
                      <div>
                        <div className="text-neutral-500">Entitled</div>
                        <div className="font-semibold">{formatLeaveBalanceAmount(getDisplayEntitled(balance) ?? 0, leaveBalanceUnit(balance))}</div>
                      </div>
                      )}
                      <div>
                        <div className="text-neutral-500">Accrued</div>
                        <div className="font-semibold">{formatLeaveBalanceAmount(getDisplayAccrued(balance), leaveBalanceUnit(balance))}</div>
                      </div>
                      <div>
                        <div className="text-neutral-500">Taken</div>
                        <div className="font-semibold text-orange-600">{formatLeaveBalanceAmount(getDisplayTaken(balance), leaveBalanceUnit(balance))}</div>
                      </div>
                      <div>
                        <div className="text-neutral-500">Pending</div>
                        <div className="font-semibold text-yellow-600">{formatLeaveBalanceAmount(getDisplayPending(balance), leaveBalanceUnit(balance))}</div>
                      </div>
                      <div>
                        <div className="text-neutral-500">Carried Forward</div>
                        <div className="font-semibold text-green-600">{formatLeaveBalanceAmount(getDisplayCarriedForward(balance), leaveBalanceUnit(balance))}</div>
                      </div>
                    </div>
                    
                    {balance.nextAccrualDate && (
                      <div className="mt-3 text-xs text-neutral-500">
                        Next accrual: {formatCompanyDate(balance.nextAccrualDate)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-neutral-400" />
                <p>No leave balances found for this employee.</p>
                <p className="text-sm mt-1">
                  {formData.leavePolicyId 
                    ? 'Leave balances will be created when the policy is saved and activated.'
                    : 'Assign a leave policy to this employee to set up leave balances.'}
                </p>
              </div>
            )}
          </div>
        </>
          )}
        </div>
      )}

      {(activeTab === 'login' || activeTab === 'personal' || activeTab === 'work' || activeTab === 'payroll') && (
        <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 rounded-lg p-6">
        {activeTab === 'work' && tabLoading.work && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        )}
        {activeTab === 'payroll' && tabLoading.payroll && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        )}
        {!((activeTab === 'work' && tabLoading.work) || (activeTab === 'payroll' && tabLoading.payroll)) && (
        <>
        {activeTab === 'login' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">Login Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">PIN *</label>
                <input
                  type="text"
                  required
                  maxLength={4}
                  value={formData.pin}
                  onChange={(e) => handleFieldChange('pin', e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">New Password (leave blank to keep current)</label>
                <input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Password Question</label>
                <input
                  type="text"
                  value={formData.passwordQuestion || ''}
                  onChange={(e) => handleFieldChange('passwordQuestion', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Password Answer</label>
                <input
                  type="text"
                  value={formData.passwordAnswer || ''}
                  onChange={(e) => handleFieldChange('passwordAnswer', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="mustChangePassword"
                checked={formData.mustChangePassword ?? false}
                onChange={(e) => handleFieldChange('mustChangePassword', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
              />
              <label htmlFor="mustChangePassword" className="ml-2 block text-sm text-neutral-700">
                Must change password on next login
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-neutral-700">
                Active
              </label>
            </div>

            <div className="mt-6 pt-6 border-t border-neutral-200 space-y-3">
              <h3 className="text-base font-semibold text-neutral-800">Multi-Factor Authentication</h3>
              {mfaLoading ? (
                <p className="text-sm text-neutral-600">Loading MFA status…</p>
              ) : mfaError ? (
                <p className="text-sm text-red-600">{mfaError}</p>
              ) : !mfaStatus?.tenantMfaEnabled ? (
                <p className="text-sm text-neutral-600">MFA is not enabled for this company.</p>
              ) : (
                <div className="space-y-3 text-sm text-neutral-700">
                  <p>
                    <span className="font-medium text-neutral-600">Requirement:</span> {mfaStatus.requirement}
                  </p>
                  <p>
                    <span className="font-medium text-neutral-600">Allowed methods:</span>{' '}
                    {mfaStatus.allowedMethods.length
                      ? mfaStatus.allowedMethods.map((m) => (m === 'Authenticator' ? 'Authenticator app' : m === 'Sms' ? 'SMS' : m)).join(', ')
                      : 'None'}
                  </p>
                  <p>
                    <span className="font-medium text-neutral-600">Enrollment:</span>{' '}
                    {mfaStatus.enrolled || mfaStatus.mfaEnabled
                      ? `Enrolled${mfaStatus.method ? ` (${mfaStatus.method === 'Authenticator' ? 'Authenticator app' : mfaStatus.method === 'Sms' ? 'SMS' : mfaStatus.method})` : ''}`
                      : 'Not enrolled'}
                  </p>
                  {mfaStatus.exemptActive && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900">
                      <p className="font-medium">Temporary exemption active</p>
                      {mfaStatus.exemptUntil && (
                        <p>Until {formatCompanyDateTime(mfaStatus.exemptUntil)}</p>
                      )}
                      {mfaStatus.exemptNote && <p>Note: {mfaStatus.exemptNote}</p>}
                    </div>
                  )}

                  {mfaStatus.canTemporarilyExempt && (
                    <div className="border border-neutral-200 rounded-md p-3 space-y-2 bg-neutral-50">
                      <p className="font-medium text-neutral-800">Temporary exemption</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="text-neutral-600">Hours</label>
                        <input
                          type="number"
                          min={1}
                          value={mfaExemptHours}
                          onChange={(e) => setMfaExemptHours(Math.max(1, parseInt(e.target.value, 10) || 1))}
                          className="w-24 px-2 py-1 border border-neutral-300 rounded-md"
                          disabled={mfaActionLoading}
                        />
                      </div>
                      <div>
                        <label className="block text-neutral-600 mb-1">Note</label>
                        <input
                          type="text"
                          value={mfaExemptNote}
                          onChange={(e) => setMfaExemptNote(e.target.value)}
                          className="w-full px-2 py-1 border border-neutral-300 rounded-md"
                          placeholder="Reason for exemption (required)"
                          disabled={mfaActionLoading}
                        />
                      </div>
                      <button
                        type="button"
                        disabled={mfaActionLoading}
                        onClick={async () => {
                          if (!id) return;
                          if (!mfaExemptNote.trim()) {
                            toast.error('A note is required when exempting MFA.');
                            return;
                          }
                          setMfaActionLoading(true);
                          try {
                            const status = await authApi.mfa.exemptEmployee(parseInt(id, 10), mfaExemptHours, mfaExemptNote.trim());
                            setMfaStatus(status);
                            toast.success('Temporary MFA exemption applied.');
                          } catch (err: any) {
                            toast.error(err?.response?.data?.message || err?.message || 'Failed to exempt.');
                          } finally {
                            setMfaActionLoading(false);
                          }
                        }}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        Apply exemption
                      </button>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {mfaStatus.exemptActive && (
                      <button
                        type="button"
                        disabled={mfaActionLoading}
                        onClick={async () => {
                          if (!id) return;
                          setMfaActionLoading(true);
                          try {
                            const status = await authApi.mfa.clearExempt(parseInt(id, 10));
                            setMfaStatus(status);
                            toast.success('Exemption cleared.');
                          } catch (err: any) {
                            toast.error(err?.response?.data?.message || err?.message || 'Failed to clear exemption.');
                          } finally {
                            setMfaActionLoading(false);
                          }
                        }}
                        className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 disabled:opacity-50"
                      >
                        Clear exemption
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={mfaActionLoading}
                      onClick={async () => {
                        if (!id) return;
                        if (!(await appConfirm('Reset this employee\'s MFA enrollment? They will need to set up MFA again.', { danger: true, confirmLabel: 'Reset' }))) return;
                        setMfaActionLoading(true);
                        try {
                          const status = await authApi.mfa.resetEmployee(parseInt(id, 10));
                          setMfaStatus(status);
                          toast.success('MFA reset.');
                        } catch (err: any) {
                          toast.error(err?.response?.data?.message || err?.message || 'Failed to reset MFA.');
                        } finally {
                          setMfaActionLoading(false);
                        }
                      }}
                      className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-md bg-white hover:bg-red-50 disabled:opacity-50"
                    >
                      Reset MFA
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Face Attendance */}
            <div className="mt-6 pt-6 border-t border-neutral-200">
              <h3 className="text-base font-semibold text-neutral-800 mb-2 flex items-center gap-2">
                <Scan className="h-4 w-4" />
                Face Attendance
              </h3>
              {employee.profileImageUrl && (
                <div className="mb-3">
                  <p className="text-sm text-neutral-600 mb-2">Profile photo (from face enrollment):</p>
                  <button
                    type="button"
                    onClick={() => setShowProfileImageModal(true)}
                    className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
                  >
                    <img
                      src={getProfileImageUrl(employee.profileImageUrl, employee.updatedAt) ?? ''}
                      alt={`${employee.firstName} ${employee.lastName}`}
                      className="w-24 h-24 rounded-full object-cover border-2 border-neutral-200 cursor-pointer hover:opacity-90 transition-opacity"
                    />
                  </button>
                </div>
              )}
              <p className="text-sm text-neutral-600 mb-3">
                {employee.faceId
                  ? 'Face enrolled. Employee can use face scan for attendance.'
                  : 'No face enrolled. Employee will need to enroll face when using face scan for attendance.'}
              </p>
              {employee.faceId && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!(await appConfirm('Delete this employee\'s face ID? They will need to enroll their face fresh the next time they use face scan for attendance.', { danger: true, confirmLabel: 'Delete' }))) return;
                    if (!id) return;
                    setDeletingFaceId(true);
                    try {
                      await faceApi.deleteFaceForEmployee(parseInt(id));
                      toast.success('Face ID deleted. Employee can enroll face fresh on next face scan.');
                      await fetchData();
                      const refreshed = await employeeApi.getById(parseInt(id!));
                      if (currentUser?.id === parseInt(id!)) {
                        updateEmployee(refreshed);
                      }
                    } catch (err: unknown) {
                      const msg = err && typeof err === 'object' && 'response' in err && (err as { response?: { data?: { message?: string } } }).response?.data?.message;
                      toast.error(typeof msg === 'string' ? msg : 'Failed to delete face ID');
                    } finally {
                      setDeletingFaceId(false);
                    }
                  }}
                  disabled={deletingFaceId}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingFaceId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  {deletingFaceId ? 'Deleting...' : 'Delete Face ID'}
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'personal' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">Personal Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Title</label>
                <select
                  value={formData.title || ''}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">—</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Miss">Miss</option>
                  <option value="Mx">Mx</option>
                  <option value="Dr">Dr</option>
                  <option value="Prof">Prof</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">First Name *</label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => handleFieldChange('firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => handleFieldChange('lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Gender</label>
                <select
                  value={formData.gender || ''}
                  onChange={(e) => handleFieldChange('gender', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer Not to Say">Prefer Not to Say</option>
                </select>
              </div>
              {formData.gender === 'Other' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Gender (Other)</label>
                  <input
                    type="text"
                    value={formData.genderOther || ''}
                    onChange={(e) => handleFieldChange('genderOther', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Birthday</label>
                <input
                  type="date"
                  value={formData.birthday || ''}
                  onChange={(e) => handleFieldChange('birthday', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Address line 1</label>
                <input
                  type="text"
                  value={formData.address1 || ''}
                  onChange={(e) => handleFieldChange('address1', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Address line 2</label>
                <input
                  type="text"
                  value={formData.address2 || ''}
                  onChange={(e) => handleFieldChange('address2', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Address line 3</label>
                <input
                  type="text"
                  value={formData.address3 || ''}
                  onChange={(e) => handleFieldChange('address3', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">County</label>
                <input
                  type="text"
                  value={formData.county || ''}
                  onChange={(e) => handleFieldChange('county', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Zip code</label>
                <input
                  type="text"
                  value={formData.zipCode || ''}
                  onChange={(e) => handleFieldChange('zipCode', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {employee?.address ? (
              <p className="text-xs text-neutral-500">
                Legacy address on file (read-only until migrated):{' '}
                <span className="whitespace-pre-wrap">{employee.address}</span>
              </p>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">PSA License</label>
                <input
                  type="text"
                  value={formData.psaLicense || ''}
                  onChange={(e) => handleFieldChange('psaLicense', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate || ''}
                  onChange={(e) => handleFieldChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Employment end date
                </label>
                <input
                  type="date"
                  value={formData.employmentEndDate || ''}
                  onChange={(e) => handleFieldChange('employmentEndDate', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Last day employed (inclusive). If this date falls inside a payroll run and the leave policy pays out on
                  termination, gross includes a <span className="font-mono">TerminationLeavePayout</span> line (PD-017).
                  Clear the date if not applicable.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Probation end date
                </label>
                <input
                  type="date"
                  value={formData.probationEndDate || ''}
                  onChange={(e) => handleFieldChange('probationEndDate', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Until this date, supervisor shifts keep the standing band year and still receive the supervisor premium
                  (C6). On/after this date, supervisor shifts use band 5 + premium. Clear if not applicable.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" id="visa-details">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Visa Number</label>
                <input
                  type="text"
                  value={formData.visaNumber || ''}
                  onChange={(e) => handleFieldChange('visaNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Visa Expiry Date</label>
                <input
                  type="date"
                  value={formData.visaExpiryDate || ''}
                  onChange={(e) => handleFieldChange('visaExpiryDate', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Comment</label>
              <textarea
                value={formData.comment || ''}
                onChange={(e) => handleFieldChange('comment', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {activeTab === 'work' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-neutral-800 mb-4">Work Information</h2>
            {employee.profileImageUrl && (
              <div className="mb-4 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                <p className="text-sm font-medium text-neutral-700 mb-2">Profile Photo (from face enrollment)</p>
                <button
                  type="button"
                  onClick={() => setShowProfileImageModal(true)}
                  className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
                >
                  <img
                    src={getProfileImageUrl(employee.profileImageUrl, employee.updatedAt) ?? ''}
                    alt={`${employee.firstName} ${employee.lastName}`}
                    className="w-24 h-24 rounded-full object-cover border-2 border-neutral-200 cursor-pointer hover:opacity-90 transition-opacity"
                  />
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Main Location *</label>
                <select
                  required
                  value={formData.mainLocationId || ''}
                  onChange={(e) => handleFieldChange('mainLocationId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Location</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Main Position *</label>
                <select
                  required
                  value={formData.mainPositionId || ''}
                  onChange={(e) => handleFieldChange('mainPositionId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Position</option>
                  {positions.map((pos) => (
                    <option key={pos.id} value={pos.id}>
                      {pos.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Employee or Manager</label>
              <div className="inline-flex rounded-md border border-neutral-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => handleFieldChange('isManager', false)}
                  className={`px-4 py-2 text-sm font-medium ${
                    !formData.isManager
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  Employee
                </button>
                <button
                  type="button"
                  onClick={() => handleFieldChange('isManager', true)}
                  className={`px-4 py-2 text-sm font-medium border-l border-neutral-300 ${
                    formData.isManager
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  Manager
                </button>
              </div>
              <p className="mt-1 text-xs text-neutral-500">
                Only Managers can be assigned as a line manager or location manager for other people.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Line manager</label>
              <AsyncSelect<LineManagerOption, false>
                cacheOptions={false}
                defaultOptions
                loadOptions={loadLineManagerOptions}
                value={lineManagerSelectValue}
                onChange={(opt: SingleValue<LineManagerOption>) => {
                  setLineManagerSelectValue(opt);
                  setFormData((prev) => ({ ...prev, lineManagerEmployeeId: opt?.value }));
                }}
                isClearable
                placeholder="Optional — search managers by name or email..."
                styles={lineManagerAsyncStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? `No managers match "${inputValue}"` : 'Type to search managers'
                }
              />
              <p className="mt-1 text-xs text-neutral-500">
                Optional. Only people marked as Manager appear here. If this is empty, the{' '}
                {(() => {
                  const loc = locations.find((l) => l.id === formData.mainLocationId);
                  const mgr = loc?.manager;
                  return mgr
                    ? `location manager (${mgr.firstName} ${mgr.lastName})`
                    : 'location manager';
                })()}{' '}
                is used. You can also pick the location manager here to set them explicitly.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Payroll Department *</label>
                <select
                  required
                  value={formData.payrollDepartmentId || ''}
                  onChange={(e) => handleFieldChange('payrollDepartmentId', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Payroll Department</option>
                  {payrollDepartments.filter(dept => dept.isActive).map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Employment classification *</label>
                <select
                  required
                  value={
                    formData.payrollEmploymentTypeId != null && formData.payrollEmploymentTypeId > 0
                      ? String(formData.payrollEmploymentTypeId)
                      : 'other'
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === 'other') {
                      setFormData((prev) => ({
                        ...prev,
                        payrollEmploymentTypeId: undefined,
                      }));
                      return;
                    }
                    const id = parseInt(v, 10);
                    const row = payrollEmploymentTypes.find((t) => t.id === id);
                    setFormData((prev) => ({
                      ...prev,
                      payrollEmploymentTypeId: id,
                      employmentType: row?.displayName ?? prev.employmentType,
                    }));
                  }}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" disabled>
                    {payrollEmploymentTypes.length ? 'Select payroll employment type' : 'Loading types…'}
                  </option>
                  {payrollEmploymentTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.displayName}
                      {t.code ? ` (${t.code})` : ''}
                    </option>
                  ))}
                  <option value="other">Other (custom label)</option>
                </select>
                {(formData.payrollEmploymentTypeId == null || formData.payrollEmploymentTypeId <= 0) && (
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-neutral-600 mb-1">Custom employment label</label>
                    <input
                      type="text"
                      value={formData.employmentType || ''}
                      onChange={(e) => handleFieldChange('employmentType', e.target.value || undefined)}
                      placeholder="e.g. legacy or non-catalog text"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                )}
                <p className="text-xs text-neutral-500 mt-1">
                  Prefer a catalog type when possible so payroll rules can use the linked employment type id.
                </p>
              </div>
            </div>

            <BasketSelect
              label="Sub Locations"
              availableItems={locations
                .filter(loc => !formData.mainLocationId || loc.id !== formData.mainLocationId)
                .map(loc => ({ id: loc.id, label: loc.name }))}
              selectedIds={formData.subLocationIds || []}
              onChange={(ids) => handleArrayFieldChange('subLocationIds', ids.map(id => id.toString()))}
              placeholder="Select locations..."
            />

            <BasketSelect
              label="Sub Positions"
              availableItems={positions
                .filter(pos => !formData.mainPositionId || pos.id !== formData.mainPositionId)
                .map(pos => ({ id: pos.id, label: pos.title }))}
              selectedIds={formData.subPositionIds || []}
              onChange={(ids) => handleArrayFieldChange('subPositionIds', ids.map(id => id.toString()))}
              placeholder="Select positions..."
            />

            <BasketSelect
              label="Schedules"
              availableItems={schedules.map(schedule => ({ id: schedule.id, label: schedule.name }))}
              selectedIds={formData.scheduleIds || []}
              onChange={(ids) => handleArrayFieldChange('scheduleIds', ids.map(id => id.toString()))}
              placeholder="Select schedules..."
            />

            <BasketSelect
              label="App Roles"
              required
              availableItems={roles.map(role => ({ id: role.id, label: role.name }))}
              selectedIds={formData.appRoleIds || []}
              onChange={(ids) => handleArrayFieldChange('appRoleIds', ids.map(id => id.toString()))}
              placeholder="Select roles..."
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                id="allowMobileClockIn"
                checked={formData.allowMobileClockIn ?? true}
                onChange={(e) => handleFieldChange('allowMobileClockIn', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
              />
              <label htmlFor="allowMobileClockIn" className="ml-2 block text-sm text-neutral-700">
                Allow mobile clock in
              </label>
            </div>
          </div>
        )}

        {activeTab === 'payroll' && employee && (
          <PayrollAndPayRecords
            employee={employee}
            payRecords={payRecords}
            locations={locations}
            positions={positions}
            schedules={schedules}
            onRefresh={refreshPayRecords}
            payrollForm={{
              worksNumber: formData.worksNumber ?? '',
              ppsNumber: formData.ppsNumber ?? '',
              ban: formData.ban ?? '',
              bic: formData.bic ?? '',
              accountNumber: formData.accountNumber ?? '',
              sortCode: formData.sortCode ?? '',
              accountName: formData.accountName ?? '',
              bankName: formData.bankName ?? '',
              isPremiumPayEligible: formData.isPremiumPayEligible ?? true,
              allowAdditiveHourlyPay: formData.allowAdditiveHourlyPay ?? false,
            }}
            onPayrollFieldChange={handleFieldChange}
          />
        )}

        <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-neutral-200">
          <button
            type="button"
            onClick={() => navigate('/employees')}
            className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
        </>
        )}
      </form>
      )}

      {/* Profile Image Modal - full size */}
      {showProfileImageModal && employee?.profileImageUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setShowProfileImageModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Profile photo full size"
        >
          <div
            className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getProfileImageUrl(employee.profileImageUrl, employee.updatedAt) ?? ''}
              alt={`${employee.firstName} ${employee.lastName} - Full size`}
              className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowProfileImageModal(false)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full text-neutral-700 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      )}

      <EmployeeHierarchyModal
        employeeId={hierarchyModalOpen && id ? parseInt(id, 10) : null}
        onClose={() => setHierarchyModalOpen(false)}
      />
    </div>
  );
};

// Pay Records Tab Component - REMOVED: Now using PayrollAndPayRecords component
// The PayRecordsTab and PayrollTab components have been removed and replaced with PayrollAndPayRecords component

// Feedback Tab Component
const FeedbackTab: React.FC<{ feedbackRecords: FeedbackRecord[]; employeeId: number; onRefresh: () => void }> = ({ feedbackRecords, employeeId, onRefresh }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<FeedbackRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<Array<{ name: string; data: string; type: string }>>([]);
  const [followUps, setFollowUps] = useState<Array<{ email: string; note: string }>>([]);
  const [formData, setFormData] = useState<CreateFeedbackRecordRequest>({
    employeeId,
    type: '',
    actionTaken: '',
    description: '',
    priority: 'Medium',
    status: 'Unresolved',
  });

  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Handle file selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments = [...attachments];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const base64 = await fileToBase64(file);
        newAttachments.push({
          name: file.name,
          data: base64,
          type: file.type
        });
      } catch (error) {
        console.error('Error converting file to base64:', error);
        toast.error(`Failed to process file: ${file.name}`);
      }
    }
    setAttachments(newAttachments);
    // Clear the input
    e.target.value = '';
  };

  // Remove attachment
  const handleRemoveAttachment = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
  };

  // Follow up helpers
  const handleAddFollowUp = () => {
    setFollowUps([...followUps, { email: '', note: '' }]);
  };

  const handleFollowUpChange = (index: number, field: 'email' | 'note', value: string) => {
    setFollowUps((prev) =>
      prev.map((fu, i) => (i === index ? { ...fu, [field]: value } : fu))
    );
  };

  const handleRemoveFollowUp = (index: number) => {
    setFollowUps((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (saving) {
      return;
    }
    
    try {
      setSaving(true);

      // Convert attachments and follow-ups to JSON strings
      const attachmentsJson = attachments.length > 0 ? JSON.stringify(attachments) : undefined;
      const followUpsJson = followUps.length > 0 ? JSON.stringify(followUps) : undefined;

      const submitData: CreateFeedbackRecordRequest = {
        ...formData,
        attachments: attachmentsJson,
        followupRecords: followUpsJson,
      };

      if (editingRecord) {
        await feedbackRecordApi.update(editingRecord.id, submitData);
      } else {
        await feedbackRecordApi.create(submitData);
      }
      setShowModal(false);
      setEditingRecord(null);
      setAttachments([]);
      setFollowUps([]);
      setFormData({ employeeId, type: '', actionTaken: '', description: '', priority: 'Medium', status: 'Unresolved' });
      await onRefresh();
      toast.success(editingRecord ? 'Feedback record updated successfully!' : 'Feedback record created successfully!');
    } catch (error: any) {
      console.error('Error saving feedback record:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save feedback record. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record: FeedbackRecord) => {
    setEditingRecord(record);
    setFormData({
      employeeId,
      type: record.type,
      actionTaken: record.actionTaken,
      description: record.description,
      priority: record.priority,
      status: record.status,
    });
    // Parse existing attachments
    if (record.attachments) {
      try {
        const parsedAttachments = JSON.parse(record.attachments);
        setAttachments(Array.isArray(parsedAttachments) ? parsedAttachments : []);
      } catch {
        setAttachments([]);
      }
    } else {
      setAttachments([]);
    }

    // Parse existing follow up records
    if (record.followupRecords) {
      try {
        const parsedFollowUps = JSON.parse(record.followupRecords);
        setFollowUps(
          Array.isArray(parsedFollowUps)
            ? parsedFollowUps.map((fu: any) => ({
                email: fu.email || '',
                note: fu.note || '',
              }))
            : []
        );
      } catch {
        setFollowUps([]);
      }
    } else {
      setFollowUps([]);
    }

    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (await appConfirm('Are you sure you want to delete this feedback record?', { danger: true, confirmLabel: 'Delete' })) {
      try {
        await feedbackRecordApi.delete(id);
        await onRefresh();
        toast.success('Feedback record deleted successfully!');
      } catch (error) {
        console.error('Error deleting feedback record:', error);
        toast.error('Failed to delete feedback record. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-neutral-800">Feedback Records</h2>
        <button
          type="button"
          onClick={() => {
            setEditingRecord(null);
            setAttachments([]);
            setFollowUps([]);
            setFormData({ employeeId, type: '', actionTaken: '', description: '', priority: 'Medium', status: 'Unresolved' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Feedback
        </button>
      </div>

      {feedbackRecords.length === 0 ? (
        <p className="text-neutral-500">No feedback records found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Action Taken</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {feedbackRecords.map((record) => (
                <tr key={record.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.type}</td>
                  <td className="px-6 py-4 text-sm text-neutral-900">{record.actionTaken}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.priority}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.status}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{formatCompanyDate(record.createdAt)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button onClick={() => handleEdit(record)} className="text-blue-600 hover:text-blue-900 mr-4">
                      <Edit className="h-4 w-4 inline" />
                    </button>
                    <button onClick={() => handleDelete(record.id)} className="text-red-600 hover:text-red-900">
                      <Trash2 className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl my-8 max-h-[80vh] overflow-y-auto">
            <div className="px-6 pt-6 pb-2 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">
                {editingRecord ? 'Edit Feedback Record' : 'Add Feedback Record'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Type *</label>
                  <input
                    type="text"
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Action Taken *</label>
                  <select
                    required
                    value={formData.actionTaken}
                    onChange={(e) => setFormData({ ...formData, actionTaken: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Action</option>
                    <option value="1st Verbal Warning">1st Verbal Warning</option>
                    <option value="1st Written Warning">1st Written Warning</option>
                    <option value="2nd Verbal Warning">2nd Verbal Warning</option>
                    <option value="2nd Written Warning">2nd Written Warning</option>
                    <option value="3rd Verbal Warning">3rd Verbal Warning</option>
                    <option value="3rd Written Warning">3rd Written Warning</option>
                    <option value="No Action Taken">No Action Taken</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Description *</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Priority *</label>
                    <select
                      required
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Status *</label>
                    <select
                      required
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="Unresolved">Unresolved</option>
                      <option value="Resolved">Resolved</option>
                      <option value="In Progress">In Progress</option>
                    </select>
                  </div>
                </div>
              {/* Attachments */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Attachments</label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className={`flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-md text-sm ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-neutral-50'}`}>
                        <Paperclip className="h-4 w-4" />
                        Add Files
                        <input
                          type="file"
                          multiple
                          onChange={handleFileChange}
                          className="hidden"
                          accept="*/*"
                          disabled={saving}
                        />
                      </label>
                    </div>
                    
                    {attachments.length > 0 && (
                      <div className="space-y-2 mt-2">
                        {attachments.map((attachment, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-neutral-50 border border-neutral-200 rounded-md">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <FileText className="h-4 w-4 text-neutral-500 flex-shrink-0" />
                              <span className="text-sm text-neutral-700 truncate" title={attachment.name}>{attachment.name}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {attachment.data && (
                                <a
                                  href={attachment.data}
                                  download={attachment.name}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Download"
                                >
                                  <FileText className="h-4 w-4" />
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveAttachment(index)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Remove"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              {/* Follow ups */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-neutral-700">Follow Up</label>
                    <button
                      type="button"
                      onClick={handleAddFollowUp}
                      disabled={saving}
                      className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      + Add Follow Up
                    </button>
                  </div>
                  {followUps.length === 0 && (
                    <p className="text-xs text-neutral-500">No follow ups added.</p>
                  )}
                  <div className="space-y-3 mt-2">
                    {followUps.map((fu, index) => (
                      <div
                        key={index}
                        className="border border-neutral-200 rounded-md p-3 space-y-2 bg-neutral-50"
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-medium text-neutral-600">
                            Follow Up #{index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFollowUp(index)}
                            disabled={saving}
                            className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 mb-1">
                              Email Address
                            </label>
                            <input
                              type="email"
                              value={fu.email}
                              onChange={(e) =>
                                handleFollowUpChange(index, 'email', e.target.value)
                              }
                              disabled={saving}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 mb-1">
                              Note
                            </label>
                            <textarea
                              rows={2}
                              value={fu.note}
                              onChange={(e) =>
                                handleFollowUpChange(index, 'note', e.target.value)
                              }
                              disabled={saving}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              <div className="flex gap-3 justify-end pt-2 border-t border-neutral-200 mt-4">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  disabled={saving}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? 'Saving...' : (editingRecord ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Notes Tab Component
const NotesTab: React.FC<{ employeeNotes: EmployeeNote[]; employeeId: number; onRefresh: () => void }> = ({ employeeNotes, employeeId, onRefresh }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState<EmployeeNote | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<CreateEmployeeNoteRequest>({
    employeeId,
    noteDate: new Date().toISOString().split('T')[0], // Default to today
    subject: '',
    content: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (saving) {
      return;
    }
    
    try {
      setSaving(true);

      if (editingNote) {
        await employeeNoteApi.update(editingNote.id, formData);
      } else {
        await employeeNoteApi.create(formData);
      }
      setShowModal(false);
      setEditingNote(null);
      setFormData({ employeeId, noteDate: new Date().toISOString().split('T')[0], subject: '', content: '' });
      await onRefresh();
      toast.success(editingNote ? 'Note updated successfully!' : 'Note created successfully!');
    } catch (error: any) {
      console.error('Error saving note:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save note. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (note: EmployeeNote) => {
    setEditingNote(note);
    setFormData({
      employeeId,
      noteDate: note.noteDate.split('T')[0],
      subject: note.subject || '',
      content: note.content || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (await appConfirm('Are you sure you want to delete this note?', { danger: true, confirmLabel: 'Delete' })) {
      try {
        await employeeNoteApi.delete(id);
        await onRefresh();
        toast.success('Note deleted successfully!');
      } catch (error) {
        console.error('Error deleting note:', error);
        toast.error('Failed to delete note. Please try again.');
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return formatCompanyDate(date);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-neutral-800">Notes</h2>
        <button
          type="button"
          onClick={() => {
            setEditingNote(null);
            setFormData({ employeeId, noteDate: new Date().toISOString().split('T')[0], subject: '', content: '' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Note
        </button>
      </div>

      {employeeNotes.length === 0 ? (
        <p className="text-neutral-500">No notes found.</p>
      ) : (
        <div className="space-y-3">
          {employeeNotes.map((note) => (
            <div key={note.id} className="border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-medium text-neutral-600">
                      {formatDate(note.noteDate)}
                    </span>
                    {note.subject && (
                      <span className="text-sm font-semibold text-neutral-800">
                        {note.subject}
                      </span>
                    )}
                  </div>
                  {note.content && (
                    <p className="text-sm text-neutral-700 whitespace-pre-wrap">
                      {note.content}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    type="button"
                    onClick={() => handleEdit(note)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Edit note"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(note.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete note"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for Add/Edit Note */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                {editingNote ? 'Edit Note' : 'Add Note'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingNote(null);
                  setFormData({ employeeId, noteDate: new Date().toISOString().split('T')[0], subject: '', content: '' });
                }}
                className="text-neutral-500 hover:text-neutral-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.noteDate}
                  onChange={(e) => setFormData({ ...formData, noteDate: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={formData.subject || ''}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter note subject (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Content
                </label>
                <textarea
                  value={formData.content || ''}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter note content (optional)"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-neutral-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingNote(null);
                    setFormData({ employeeId, noteDate: new Date().toISOString().split('T')[0], subject: '', content: '' });
                  }}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={saving}
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingNote ? 'Update' : 'Create'} Note
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Training Tab Component
const TrainingTab: React.FC<{ trainingRecords: TrainingRecord[]; employeeId: number; trainingTypes: TrainingType[]; onRefresh: () => void }> = ({ trainingRecords, employeeId, trainingTypes, onRefresh }) => {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TrainingRecord | null>(null);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringRecord, setRecurringRecord] = useState<TrainingRecord | null>(null);
  const [showCycleModal, setShowCycleModal] = useState(false);
  const [editingCycle, setEditingCycle] = useState<TrainingRecordCycle | null>(null);
  const [recordForCycle, setRecordForCycle] = useState<TrainingRecord | null>(null);
  const [cycleFormData, setCycleFormData] = useState<CreateTrainingRecordCycleRequest>({ status: 'Pending' });
  const [savingCycle, setSavingCycle] = useState(false);
  const [attachments, setAttachments] = useState<Array<{ name: string; data: string; type: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<'Pending' | 'Completed' | 'Unassigned'>('Pending');
  const [formData, setFormData] = useState<CreateTrainingRecordRequest>({
    employeeId,
    trainingTypeId: 0,
    status: 'Pending',
  });

  // Training has its own status (backend auto-updates from cycles: Pending/In Progress/Overdue; Completed is admin-only).
  const getDisplayStatus = (r: TrainingRecord): string => r.status || 'Pending';

  const formatRecurringFrequency = (type?: TrainingType | null): string | null => {
    if (!type?.frequency || type.frequency !== 'Recurring') return null;
    if (type.recurringValue && type.recurringUnit) {
      const val = type.recurringValue;
      const unit = type.recurringUnit === 'days' ? (val === 1 ? 'day' : 'days')
        : type.recurringUnit === 'weeks' ? (val === 1 ? 'week' : 'weeks')
        : type.recurringUnit === 'months' ? (val === 1 ? 'month' : 'months')
        : type.recurringUnit;
      return `Every ${val} ${unit}`;
    }
    return 'Recurring';
  };

  // Cycle count and status breakdown for recurring (e.g. "3 cycles: 2 Pending, 1 Completed")
  const getCycleStatusBreakdown = (r: TrainingRecord): string => {
    const cycles = r.cycles || [];
    if (cycles.length === 0) return '0 cycles';
    const byStatus: Record<string, number> = {};
    cycles.forEach((c) => {
      const s = (c.status || 'Pending');
      byStatus[s] = (byStatus[s] || 0) + 1;
    });
    return `${cycles.length} cycle${cycles.length !== 1 ? 's' : ''}: ${Object.entries(byStatus).map(([k, v]) => `${v} ${k}`).join(', ')}`;
  };

  const pendingStatuses = ['Pending', 'In Progress', 'Overdue'];
  const pendingRecords = trainingRecords.filter((r) =>
    pendingStatuses.map((s) => s.toLowerCase()).includes((r.status || '').toLowerCase())
  );
  const completedRecords = trainingRecords.filter(
    (r) => (r.status || '').toLowerCase() === 'completed'
  );

  const usedTrainingTypeIds = new Set<number>(
    trainingRecords.map((r) => r.trainingTypeId).filter((id) => !!id)
  );
  const unassignedTrainingTypes = trainingTypes.filter(
    (tt) => !usedTrainingTypeIds.has(tt.id)
  );

  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Handle file selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments = [...attachments];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const base64 = await fileToBase64(file);
        newAttachments.push({
          name: file.name,
          data: base64,
          type: file.type
        });
      } catch (error) {
        console.error('Error converting file to base64:', error);
        toast.error(`Failed to process file: ${file.name}`);
      }
    }
    setAttachments(newAttachments);
    // Clear the input
    e.target.value = '';
  };

  // Remove attachment
  const handleRemoveAttachment = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (saving) {
      return;
    }
    
    try {
      if (!formData.trainingTypeId || formData.trainingTypeId === 0) {
        toast.warning('Please select a training type.');
        return;
      }
      
      setSaving(true);
      
      // Convert attachments to JSON string
      const attachmentsJson = attachments.length > 0 ? JSON.stringify(attachments) : undefined;
      const submitData = {
        ...formData,
        attachments: attachmentsJson
      };
      
      console.log('Saving training record with data:', submitData);
      const selectedType = trainingTypes.find((t) => t.id === formData.trainingTypeId);
      const isRecurring = selectedType?.frequency === 'Recurring';

      let result;
      if (editingRecord) {
        result = await trainingRecordApi.update(editingRecord.id, submitData);
        console.log('Training record updated:', result);
      } else {
        result = await trainingRecordApi.create(submitData);
        console.log('Training record created:', result);
      }
      setShowModal(false);
      setEditingRecord(null);
      setAttachments([]);
      setFormData({ employeeId, trainingTypeId: 0, status: 'Pending' });
      await onRefresh();
      toast.success(editingRecord ? 'Training record updated successfully!' : 'Training record created successfully!');

      if (!editingRecord && isRecurring && result) {
        setRecurringRecord(result);
        setShowRecurringModal(true);
      }
      console.log('Training records refreshed after save');
    } catch (error: any) {
      console.error('Error saving training record:', error);
      console.error('Error details:', error?.response?.data);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save training record. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record: TrainingRecord) => {
    if (record.trainingType?.frequency === 'Recurring') {
      setRecurringRecord(record);
      setShowRecurringModal(true);
    } else {
      setEditingRecord(record);
      setFormData({
        employeeId,
        trainingTypeId: record.trainingTypeId,
        comment: record.comment || '',
        dueDate: record.dueDate ? record.dueDate.split('T')[0] : '',
        completionDate: record.completionDate ? record.completionDate.split('T')[0] : '',
        status: record.status,
      });
      if (record.attachments) {
        try {
          const parsedAttachments = JSON.parse(record.attachments);
          setAttachments(Array.isArray(parsedAttachments) ? parsedAttachments : []);
        } catch {
          setAttachments([]);
        }
      } else {
        setAttachments([]);
      }
      setShowModal(true);
    }
  };

  const handleAddCycle = (record: TrainingRecord) => {
    setEditingCycle(null);
    setRecordForCycle(record);
    setCycleFormData({ status: 'Pending' });
    setShowCycleModal(true);
  };

  const handleEditCycle = (record: TrainingRecord, cycle: TrainingRecordCycle) => {
    setRecordForCycle(record);
    setEditingCycle(cycle);
    setCycleFormData({
      dueDate: cycle.dueDate ? cycle.dueDate.split('T')[0] : '',
      completionDate: cycle.completionDate ? cycle.completionDate.split('T')[0] : '',
      status: cycle.status,
      comment: cycle.comment || '',
    });
    setShowCycleModal(true);
  };

  const handleCycleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recordForCycle || savingCycle) return;

    // Validate: cycle due date must be <= training due date (when training has due date)
    if (cycleFormData.dueDate && recordForCycle.dueDate) {
      const cycleDue = new Date(cycleFormData.dueDate);
      const trainingDue = new Date(recordForCycle.dueDate.split('T')[0] || recordForCycle.dueDate);
      if (cycleDue > trainingDue) {
        toast.error('Cycle due date must be on or before the training due date.');
        return;
      }
    }

    setSavingCycle(true);
    try {
      if (editingCycle) {
        await trainingRecordCycleApi.update(recordForCycle.id, editingCycle.id, {
          dueDate: cycleFormData.dueDate || undefined,
          completionDate: cycleFormData.completionDate || undefined,
          status: cycleFormData.status || 'Pending',
          comment: cycleFormData.comment,
        });
        toast.success('Cycle updated successfully!');
      } else {
        await trainingRecordCycleApi.create(recordForCycle.id, {
          dueDate: cycleFormData.dueDate || undefined,
          completionDate: cycleFormData.completionDate || undefined,
          status: cycleFormData.status || 'Pending',
          comment: cycleFormData.comment,
        });
        toast.success('Cycle added successfully!');
      }
      setShowCycleModal(false);
      setRecordForCycle(null);
      setEditingCycle(null);
      await onRefresh();
      if (showRecurringModal && recurringRecord) {
        const updated = await trainingRecordApi.getById(recurringRecord.id);
        setRecurringRecord(updated);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.response?.data || 'Failed to save cycle');
    } finally {
      setSavingCycle(false);
    }
  };

  const handleDeleteCycle = async (recordId: number, cycleId: number) => {
    if (!(await appConfirm('Delete this cycle?', { danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await trainingRecordCycleApi.delete(recordId, cycleId);
      toast.success('Cycle deleted');
      await onRefresh();
      if (showRecurringModal && recurringRecord?.id === recordId) {
        const updated = await trainingRecordApi.getById(recordId);
        setRecurringRecord(updated);
      }
    } catch (error: any) {
      toast.error('Failed to delete cycle');
    }
  };

  const handleDelete = async (id: number) => {
    if (await appConfirm('Are you sure you want to delete this training record?', { danger: true, confirmLabel: 'Delete' })) {
      try {
        await trainingRecordApi.delete(id);
        await onRefresh();
        toast.success('Training record deleted successfully!');
      } catch (error) {
        console.error('Error deleting training record:', error);
        toast.error('Failed to delete training record. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-neutral-800">Training Records</h2>
        <button
          type="button"
          onClick={() => {
            setActiveSubTab('Pending');
            setEditingRecord(null);
            setAttachments([]);
            setFormData({ employeeId, trainingTypeId: 0, status: 'Pending' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Training
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-neutral-200 mb-4">
        {(['Pending', 'Completed', 'Unassigned'] as const).map((tab) => {
          let count = 0;
          if (tab === 'Pending') count = pendingRecords.length;
          if (tab === 'Completed') count = completedRecords.length;
          if (tab === 'Unassigned') count = unassignedTrainingTypes.length;

          const label =
            tab === 'Unassigned'
              ? `Unassigned Training Types (${count})`
              : `${tab} (${count})`;

          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveSubTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeSubTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Pending / In Progress / Overdue */}
      {activeSubTab === 'Pending' && (
        <>
          {pendingRecords.length === 0 ? (
            <p className="text-neutral-500">
              No pending, in-progress, or overdue training records found.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Training Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Completed Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {pendingRecords.map((record) => (
                    <tr key={record.id}>
                      <td className="px-6 py-4 text-sm text-neutral-900">
                        <div>{record.trainingType?.name || 'N/A'}</div>
                        {record.trainingType?.frequency === 'Recurring' && (
                          <div className="text-xs text-neutral-500 mt-1">{getCycleStatusBreakdown(record)}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.dueDate ? formatDateOnly(record.dueDate) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.completionDate
                          ? formatDateOnly(record.completionDate)
                          : record.trainingType?.frequency === 'Recurring' && (record.cycles?.length ?? 0) > 0
                            ? '—'
                            : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {getDisplayStatus(record)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(record)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          <Edit className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => handleDelete(record.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Completed */}
      {activeSubTab === 'Completed' && (
        <>
          {completedRecords.length === 0 ? (
            <p className="text-neutral-500">No completed training records found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Training Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Completed Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {completedRecords.map((record) => (
                    <tr key={record.id}>
                      <td className="px-6 py-4 text-sm text-neutral-900">
                        <div>{record.trainingType?.name || 'N/A'}</div>
                        {record.trainingType?.frequency === 'Recurring' && (
                          <div className="text-xs text-neutral-500 mt-1">{getCycleStatusBreakdown(record)}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.dueDate ? formatDateOnly(record.dueDate) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {record.completionDate
                          ? formatDateOnly(record.completionDate)
                          : record.trainingType?.frequency === 'Recurring' && (record.cycles?.length ?? 0) > 0
                            ? '—'
                            : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {getDisplayStatus(record)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(record)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          <Edit className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => handleDelete(record.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Unassigned Training Types */}
      {activeSubTab === 'Unassigned' && (
        <>
          {unassignedTrainingTypes.length === 0 ? (
            <p className="text-neutral-500">
              All training types have at least one training record for this employee.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Training Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Frequency
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {unassignedTrainingTypes.map((type) => (
                    <tr key={type.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {type.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-900">
                        {type.description || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {type.frequency === 'One-Time'
                          ? 'One-Time'
                          : `${type.recurringValue ?? ''} ${type.recurringUnit ?? ''}`.trim()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8">
            <h3 className="text-lg font-semibold mb-4">{editingRecord ? 'Edit Training Record' : 'Add Training Record'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Training Type *</label>
                <select
                  required
                  value={formData.trainingTypeId}
                  onChange={(e) => setFormData({ ...formData, trainingTypeId: parseInt(e.target.value) })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value={0}>Select Training Type</option>
                  {trainingTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              {formData.trainingTypeId > 0 && (() => {
                const selectedType = trainingTypes.find((t) => t.id === formData.trainingTypeId);
                if (selectedType?.frequency === 'Recurring') {
                  const freq = formatRecurringFrequency(selectedType);
                  return (
                    <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-md space-y-1">
                      <p className="text-sm font-medium text-neutral-700">Recurring training details</p>
                      {freq && <p className="text-sm text-neutral-600">Frequency: {freq}</p>}
                      {selectedType.description && <p className="text-sm text-neutral-600">Description: {selectedType.description}</p>}
                    </div>
                  );
                }
                return null;
              })()}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Comment</label>
                <textarea
                  rows={4}
                  value={formData.comment || ''}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={formData.dueDate || ''}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Status *</label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Overdue">Overdue</option>
                  </select>
                </div>
              </div>
              {formData.status === 'Completed' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Completed Date</label>
                  <input
                    type="date"
                    value={formData.completionDate || ''}
                    onChange={(e) => setFormData({ ...formData, completionDate: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Attachments</label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className={`flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-md text-sm ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-neutral-50'}`}>
                      <Paperclip className="h-4 w-4" />
                      Add Files
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        accept="*/*"
                        disabled={saving}
                      />
                    </label>
                  </div>
                  
                  {attachments.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-neutral-50 border border-neutral-200 rounded-md">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText className="h-4 w-4 text-neutral-500 flex-shrink-0" />
                            <span className="text-sm text-neutral-700 truncate" title={attachment.name}>{attachment.name}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {attachment.data && (
                              <a
                                href={attachment.data}
                                download={attachment.name}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Download"
                              >
                                <FileText className="h-4 w-4" />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  disabled={saving}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? 'Saving...' : (editingRecord ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Recurring Training Edit Modal - lists cycles, Add Cycle only here */}
      {showRecurringModal && recurringRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">
                Recurring Training: {recurringRecord.trainingType?.name}
              </h3>
              <button
                type="button"
                onClick={() => { setShowRecurringModal(false); setRecurringRecord(null); }}
                className="p-2 text-neutral-500 hover:text-neutral-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4 p-3 bg-neutral-50 border border-neutral-200 rounded-md space-y-1">
              <p className="text-sm font-medium text-neutral-700">Recurring training details</p>
              {formatRecurringFrequency(recurringRecord.trainingType) && (
                <p className="text-sm text-neutral-600">Frequency: {formatRecurringFrequency(recurringRecord.trainingType)}</p>
              )}
              {recurringRecord.trainingType?.description && (
                <p className="text-sm text-neutral-600">Description: {recurringRecord.trainingType.description}</p>
              )}
            </div>
            <div className="mb-4 flex items-center gap-2">
              {recurringRecord.dueDate ? (
                <p className="text-sm text-neutral-600">
                  Training due date: {formatDateOnly(recurringRecord.dueDate)} (cycle due dates must be on or before this)
                </p>
              ) : (
                <p className="text-sm text-amber-600">
                  Set a training due date when editing the record so cycle due dates can be validated.
                </p>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowRecurringModal(false);
                  setEditingRecord(recurringRecord);
                  setFormData({
                    employeeId,
                    trainingTypeId: recurringRecord.trainingTypeId,
                    comment: recurringRecord.comment || '',
                    dueDate: recurringRecord.dueDate ? recurringRecord.dueDate.split('T')[0] : '',
                    completionDate: recurringRecord.completionDate ? recurringRecord.completionDate.split('T')[0] : '',
                    status: recurringRecord.status,
                  });
                  setAttachments([]);
                  setShowModal(true);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Edit record
              </button>
            </div>
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-neutral-800">Cycles</h4>
                <button
                  type="button"
                  onClick={() => handleAddCycle(recurringRecord)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Add Cycle
                </button>
              </div>
              {(!recurringRecord.cycles || recurringRecord.cycles.length === 0) ? (
                <p className="text-sm text-neutral-500 py-4">No cycles yet. Click Add Cycle to create one.</p>
              ) : (
                <div className="border border-neutral-200 rounded-md overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-neutral-600">Due Date</th>
                        <th className="px-4 py-2 text-left font-medium text-neutral-600">Status</th>
                        <th className="px-4 py-2 text-left font-medium text-neutral-600">Completed</th>
                        <th className="px-4 py-2 text-left font-medium text-neutral-600">Comment</th>
                        <th className="px-4 py-2 text-right font-medium text-neutral-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200">
                      {recurringRecord.cycles!.map((c) => (
                        <tr key={c.id} className="hover:bg-neutral-50">
                          <td className="px-4 py-2">{c.dueDate ? formatDateOnly(c.dueDate) : '—'}</td>
                          <td className="px-4 py-2">{c.status}</td>
                          <td className="px-4 py-2">{c.completionDate ? formatDateOnly(c.completionDate) : '—'}</td>
                          <td className="px-4 py-2 max-w-[200px] truncate" title={c.comment || ''}>{c.comment || '—'}</td>
                          <td className="px-4 py-2 text-right">
                            <button onClick={() => handleEditCycle(recurringRecord, c)} className="text-blue-600 hover:text-blue-800 mr-2">
                              <Edit className="h-4 w-4 inline" />
                            </button>
                            <button onClick={() => handleDeleteCycle(recurringRecord.id, c.id)} className="text-red-600 hover:text-red-800">
                              <Trash2 className="h-4 w-4 inline" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { setShowRecurringModal(false); setRecurringRecord(null); }}
                className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-md hover:bg-neutral-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showCycleModal && recordForCycle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg my-8">
            <h3 className="text-lg font-semibold mb-4">
              {editingCycle ? 'Edit Cycle' : 'Add Cycle'} - {recordForCycle.trainingType?.name}
            </h3>
            {recordForCycle.dueDate && (
              <p className="text-xs text-amber-600 mb-2">Cycle due date must be on or before training due date: {formatDateOnly(recordForCycle.dueDate)}</p>
            )}
            <form onSubmit={handleCycleSubmit} className="space-y-4" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={cycleFormData.dueDate || ''}
                    onChange={(e) => setCycleFormData({ ...cycleFormData, dueDate: e.target.value })}
                    disabled={savingCycle}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Status *</label>
                  <select
                    required
                    value={cycleFormData.status || 'Pending'}
                    onChange={(e) => setCycleFormData({ ...cycleFormData, status: e.target.value })}
                    disabled={savingCycle}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Overdue">Overdue</option>
                  </select>
                </div>
              </div>
              {cycleFormData.status === 'Completed' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Completion Date</label>
                  <input
                    type="date"
                    value={cycleFormData.completionDate || ''}
                    onChange={(e) => setCycleFormData({ ...cycleFormData, completionDate: e.target.value })}
                    disabled={savingCycle}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Comment</label>
                <textarea
                  rows={3}
                  value={cycleFormData.comment || ''}
                  onChange={(e) => setCycleFormData({ ...cycleFormData, comment: e.target.value })}
                  disabled={savingCycle}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => { setShowCycleModal(false); setRecordForCycle(null); setEditingCycle(null); }}
                  disabled={savingCycle}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingCycle}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  {savingCycle && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingCycle ? 'Update' : 'Add Cycle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeProfile;

